import type { Element, DOMNode } from "html-react-parser";
import type { ChildNode as DomHandlerChildNode } from "domhandler";
import type { TimelineItem } from "../types/types";

export const hasClass = (el: Element, name: string) => {
  const cls = el.attribs?.class || "";
  return cls.split(/\s+/).includes(name);
};

export const isTag = (n: DOMNode | DomHandlerChildNode): n is Element =>
  (n as Element).type === "tag";

/**
 * Convert HTML attributes to React props.
 * Handles: class -> className, style string -> style object
 */
export const htmlAttribsToReactProps = (
  attribs: Record<string, string> | undefined
): Record<string, string | React.CSSProperties> => {
  if (!attribs) return {};
  const props: Record<string, string | React.CSSProperties> = {};
  for (const [key, value] of Object.entries(attribs)) {
    if (key === "class") {
      props.className = value;
    } else if (key === "style" && typeof value === "string") {
      // Convert style string to object
      const styleObj: Record<string, string> = {};
      value.split(";").forEach((decl) => {
        const [prop, val] = decl.split(":").map((s) => s.trim());
        if (prop && val) {
          // Convert kebab-case to camelCase
          const camelProp = prop.replace(/-([a-z])/g, (_, letter) =>
            letter.toUpperCase()
          );
          styleObj[camelProp] = val;
        }
      });
      props.style = styleObj as React.CSSProperties;
    } else {
      props[key] = value;
    }
  }
  return props;
};

/** Parse dates in dd-mm-yyyy format into a comparable number (ms since epoch). */
export function parseDkDate(d?: string): number {
  if (!d) return Number.NaN;
  const m = d.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (!m) return Number.NaN;
  const [_, dd, mm, yyyy] = m;
  const date = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
  return date.getTime();
}

export type GroupedTimeline = Map<string, TimelineItem[]>;

export function groupAndSortTimeline(
  items: TimelineItem[] = []
): GroupedTimeline {
  const map: GroupedTimeline = new Map();
  for (const it of items) {
    if (!it?.targetDivId) continue;
    if (!map.has(it.targetDivId)) map.set(it.targetDivId, []);
    map.get(it.targetDivId)!.push(it);
  }
  // sort each bucket by startDate asc, then endDate asc (undefined at end)
  for (const [_k, arr] of map) {
    arr.sort((a, b) => {
      const aStart = parseDkDate(a.startDate);
      const bStart = parseDkDate(b.startDate);
      if (!Number.isNaN(aStart) && !Number.isNaN(bStart) && aStart !== bStart) {
        return aStart - bStart;
      }
      const aEnd = parseDkDate(a.endDate);
      const bEnd = parseDkDate(b.endDate);
      if (Number.isNaN(aEnd) && Number.isNaN(bEnd)) return 0;
      if (Number.isNaN(aEnd)) return 1; // undefined/NaN end dates last
      if (Number.isNaN(bEnd)) return -1;
      return aEnd - bEnd;
    });
  }
  return map;
}

export const clamp = (n: number, min: number, max: number) =>
  Math.max(min, Math.min(max, n));
